import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWith2FA, register } = useAuth();

  const [tab, setTab] = useState(0); // 0 = Login, 1 = Register
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState('');
  const [code, setCode] = useState('');

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'customer'
  });

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
    setError('');
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    setRegisterForm({ ...registerForm, [e.target.name as string]: e.target.value });
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!loginForm.email || !loginForm.password) {
        throw new Error('Please fill in all fields');
      }

      const result = await login(loginForm.email, loginForm.password);
      console.log('Login result:', result);

      if (result && result.requires2FA) {
        console.log('2FA required, showing authenticator screen');
        setRequires2FA(true);
        setUserId(result.userId);
        setSuccess('Please enter the code from your Google Authenticator app.');
        setLoading(false);
      } else {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!code || code.length !== 6) {
        throw new Error('Please enter a valid 6-digit code');
      }

      await loginWith2FA(userId, code);
      setSuccess('Login successful! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation
      if (!registerForm.name || !registerForm.email || !registerForm.phone || !registerForm.password) {
        throw new Error('Please fill in all fields');
      }

      if (registerForm.password !== registerForm.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (registerForm.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      await register(
        registerForm.name,
        registerForm.email,
        registerForm.phone,
        registerForm.password,
        registerForm.role
      );

      setSuccess('Registration successful! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Quick login buttons for demo
  const handleQuickLogin = async (role: string) => {
    const demoCredentials: Record<string, { email: string; password: string }> = {
      admin: { email: 'admin@bitehub.com', password: '1234567890' },
      merchant: { email: 'merchant@bitehub.com', password: '1234567890' },
      customer: { email: 'customer@bitehub.com', password: '1234567890' },
      delivery: { email: 'delivery@bitehub.com', password: '1234567890' }
    };

    const credentials = demoCredentials[role];
    if (credentials) {
      setLoginForm(credentials);
      setError('');
      setSuccess('');

      // Auto submit after a brief moment
      setTimeout(async () => {
        setLoading(true);
        try {
          const result = await login(credentials.email, credentials.password);
          console.log('Quick login result:', result);

          if (result && result.requires2FA) {
            console.log('2FA required for quick login');
            setRequires2FA(true);
            setUserId(result.userId);
            setSuccess('Please enter the code from your Google Authenticator app.');
            setLoading(false);
          } else {
            setSuccess(`Logged in as ${role}! Redirecting...`);
            setTimeout(() => navigate('/dashboard'), 1000);
          }
        } catch (err: any) {
          setError(err.message || 'Quick login failed');
          setLoading(false);
        }
      }, 300);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        // faint brand-green glow so the neutral canvas isn't sterile
        backgroundImage:
          'radial-gradient(60% 50% at 50% 0%, rgba(11,110,79,0.06) 0%, rgba(11,110,79,0) 70%)',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 1px 2px rgba(17,17,15,0.04), 0 18px 40px rgba(17,17,15,0.08)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="BiteHub"
                sx={{
                  width: 84,
                  height: 84,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: 3,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                Welcome back
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Keep Good Food in the Loop
              </Typography>
            </Box>

            {/* Tabs for Login/Register (Hide when 2FA is active) */}
            {!requires2FA && (
              <Tabs
                value={tab}
                onChange={(_, newValue) => {
                  setTab(newValue);
                  setError('');
                  setSuccess('');
                }}
                variant="fullWidth"
                sx={{ mb: 3 }}
              >
                <Tab label="Login" />
                <Tab label="Register" />
              </Tabs>
            )}

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {/* 2FA Verification Form */}
            {requires2FA && (
              <Box component="form" onSubmit={handleVerify2FA}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <SecurityIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Two-Factor Authentication
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter the 6-digit code from your Google Authenticator app
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Authenticator Code"
                  name="code"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) {
                      setCode(value);
                      setError('');
                    }
                  }}
                  margin="normal"
                  required
                  autoFocus
                  slotProps={{
                    htmlInput: {
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
                    }
                  }}
                  placeholder="000000"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || code.length !== 6}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => {
                    setRequires2FA(false);
                    setCode('');
                    setUserId('');
                    setError('');
                    setSuccess('');
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}

            {/* Login Form */}
            {tab === 0 && !requires2FA && (
              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={loginForm.email}
                  onChange={handleLoginChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>

                {/* Quick Login Demo Buttons */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Quick Demo Login:
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => handleQuickLogin('admin')}>
                      Admin
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleQuickLogin('merchant')}>
                      Merchant
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleQuickLogin('customer')}>
                      Customer
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleQuickLogin('delivery')}>
                      Delivery
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Register Form */}
            {tab === 1 && !requires2FA && (
              <Box component="form" onSubmit={handleRegister}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={registerForm.name}
                  onChange={handleRegisterChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={registerForm.email}
                  onChange={handleRegisterChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={registerForm.phone}
                  onChange={handleRegisterChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={registerForm.role}
                    onChange={handleRegisterChange as any}
                    label="Role"
                  >
                    <MenuItem value="customer">Customer</MenuItem>
                    <MenuItem value="merchant">Merchant</MenuItem>
                    <MenuItem value="delivery">Delivery Person</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={registerForm.password}
                  onChange={handleRegisterChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={registerForm.confirmPassword}
                  onChange={handleRegisterChange}
                  margin="normal"
                  required
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 3 }}
                >
                  {loading ? 'Registering...' : 'Register'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
          © 2026 BiteHub. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
